// Module-level confirmation-dialog store — deliberately the same pattern as
// lib/toast.ts (a tiny dependency-free store rendered once by a root component).
//
// Why this exists: destructive actions across the app used the native
// window.confirm(), which is unstyled, ignores the OnLogic visual language, and
// can't show rich context. This lets any code — component or not — trigger one
// styled dialog and await a boolean, as a near drop-in for window.confirm():
//
//   import { confirmDialog } from '../lib/confirm';
//   if (!(await confirmDialog({ title: 'Delete door?', destructive: true }))) return;
//   await doDelete();
//
// A single <ConfirmRoot /> (mounted in AppShell next to <Toaster />) subscribes
// and renders the active request. Generalizes the old DeleteResourceDialog.

import type { ReactNode } from 'react';

export interface ConfirmOptions {
  /** Dialog heading (the question). */
  title: string;
  /** Supporting copy under the title. */
  message?: ReactNode;
  /** Optional boxed detail (e.g. the name/URL of the thing being acted on). */
  detail?: ReactNode;
  /** Confirm button label. Default 'Confirm'. */
  confirmLabel?: string;
  /** Cancel button label. Default 'Cancel'. */
  cancelLabel?: string;
  /**
   * Destructive styling (salmon button + warning icon) and alertdialog
   * semantics. Defaults to true — confirmations in this app are almost always
   * destructive (deletes, cancellations). Pass false for a neutral confirm.
   */
  destructive?: boolean;
}

export interface ConfirmRequest extends ConfirmOptions {
  id: string;
  resolve: (result: boolean) => void;
}

type Listener = (request: ConfirmRequest | null) => void;

// At most one dialog is shown at a time; a new request supersedes any open one.
let current: ConfirmRequest | null = null;
const listeners = new Set<Listener>();
let nextId = 0;

function emit(): void {
  for (const listener of listeners) listener(current);
}

/** Subscribe to the active confirm request; called immediately with the
 *  current value and on every change. Returns an unsubscribe function. */
export function subscribeConfirm(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Show a confirmation dialog. Resolves true if the user confirms, false if
 * they cancel, dismiss, or press Escape.
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  // Last call wins: if a dialog is already open, treat it as cancelled so its
  // awaiter doesn't hang.
  if (current) {
    current.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    current = { ...options, id: `confirm-${(nextId += 1)}`, resolve };
    emit();
  });
}

/** Resolve the active request and clear it. Called by <ConfirmRoot />. The id
 *  guard prevents a stale button click from settling a newer dialog. */
export function settleConfirm(id: string, result: boolean): void {
  if (current && current.id === id) {
    current.resolve(result);
    current = null;
    emit();
  }
}
